import { Injectable } from '@angular/core';

import { BetSlipType } from '@bpos';
import { PlaceBetRequest } from '@bpos/v2/bet-placement';
import { BetslipConfig, EpcotConfig, RealityCheckConfig } from '@frontend/sports/common/client-config-data-access';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { ModelPopupTypes, MyBetsUrlParam } from '@frontend/sports/navigation/core/feature/url-helper';
import { PlaceBetStatus } from '@frontend/sports/types/betslip';
import { BetslipDisplayMode } from '@frontend/sports/types/components/bet-placement';
import { UserActions } from '@frontend/sports/user/feature';
import { BettingStatus, BettingStatusService } from '@frontend/vanilla/shared/betting-status';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { Observable, Subscription, concatMap, filter, first, map, switchMap, take, tap } from 'rxjs';

import { ArcRestrictionService } from '../../../../lib/arc/arc-restriction.service';
import { BaseBetslipEvents } from '../../../../lib/betslip-base/events/events';
import { EventBridgeService } from '../../../../lib/event-bridge/event-bridge.service';
import { MyBetsSummaryService } from '../../../../lib/my-bets-base/my-bets-summary.service';
import { PopupManager } from '../../../../lib/popup/popup-manager.service';
import { RealityCheckEvent, RealityCheckService } from '../../../../lib/user/services/reality-check.service';
import BetslipActions from '../../../base/store/actions';
import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState, IBetslipState } from '../../../base/store/state';
import { isLinear } from '../../../base/utils/linear-utils';
import { BetslipState } from '../../../core/betslip-state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { PickId } from '../../../core/picks/pick-id';
import { OverAskRejectReason } from '../../../model/over-ask/over-ask';
import BetbuilderActions from '../../bet-builder/actions';
import { BetslipPlaceBetActions, PlaceBetFailedReason } from '../../betplacement/actions';
import { IFailedSlipResult, IPlacedBetslipResult, ISucceededPartialSlipResult, ISucceededSlipResult } from '../../betplacement/models';
import { isFailedSlip, isSucceededNonPartialSlip, isSucceededSlip } from '../../betplacement/utils';
import BetslipComboBetActions from '../../combo-bet/actions';
import { BetslipOverAskActions } from '../../over-ask/actions';
import { OverAskService } from '../../over-ask/services/over-ask.service';
import BetslipPickActions from '../../picks/actions';
import { QuickBetActions } from '../../quick-bet/quick-bet-actions';
import { RewardTokensActions } from '../../reward-tokens/actions';
import BetslipSingleBetActions from '../../single-bet/actions';
import BetslipSystemBetActions from '../../system-bet/actions';
import { BetslipTeaserActions } from '../../teaser-bet/actions';
import { BetslipTeaserBetPlaceBetActions } from '../../teaser-bet/place-bet.actions';
import BetslipErrorsActions from '../../validation/actions';
import { BetslipErrorBetslipUpdaterService } from '../../validation/services/updaters/betslip-error-betslip-updater.service';
import { getStakeLimitErrorInfo } from '../../validation/services/utils/stake-limit-errors-info-utils';
import { BetslipResultActions } from '../actions';
import { ResultSummaryProcesserService } from '../services/result-summary-processer.service';
import { IBetslipResultState } from '../state';
import { getFailedSlipErrors, isBetBuilderSucceededSlip, isTeaserSucceededSlip } from '../utils/placed-slip.utils';

@Injectable({ providedIn: 'root' })
export default class PlaceBetResultEffects {
    private hideOverAsk: boolean;
    private arcSubscription: Subscription;

    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private resultSummaryService: ResultSummaryProcesserService,
        private overAskService: OverAskService,
        private arcRestrictionService: ArcRestrictionService,
        private betslipErrorBetslipUpdaterService: BetslipErrorBetslipUpdaterService,
        private dispatcher: DispatcherService,
        private realityCheckService: RealityCheckService,
        private realityCheckConfig: RealityCheckConfig,
        private myBetsSummary: MyBetsSummaryService,
        private eventBridgeService: EventBridgeService,
        private redirectHelper: RedirectHelperService,
        private popupManager: PopupManager,
        private epcotConfig: EpcotConfig,
        private betStatusService: BettingStatusService,
        private betslipConfig: BetslipConfig,
    ) {
        this.arcSubscription = this.arcRestrictionService.overAskEnabled$.subscribe((state) => {
            this.hideOverAsk = state;
        });
    }

    handleFinishPlaceBet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPlaceBetActions.finishPlaceBet),
            concatMap((action) => {
                const showSportcastAsCombo = this.betslipConfig.isSportcastAsComboEnabled;

                if (action.placeBetResult.status === PlaceBetStatus.Success) {
                    return this.successPlacedBetActions(
                        action.placeBetResult,
                        action.state.picks.pickList,
                        isLinear(action.state),
                        showSportcastAsCombo,
                    );
                }

                return this.failedPlacedBetActions(
                    action.placeBetResult,
                    action.request,
                    action.state.picks.pickList,
                    isLinear(action.state),
                    showSportcastAsCombo,
                );
            }),
        );
    });

    handlePlaceBetSucceeded$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipPlaceBetActions.placeBetSucceeded),
                switchMap(() =>
                    this.betStatusService.bettingStatus.pipe(
                        take(1),
                        tap((bettingStatus: BettingStatus | null) => {
                            if (!bettingStatus?.hasBets) {
                                this.betStatusService.refresh(false);
                            }
                        }),
                    ),
                ),
                tap((_) => {
                    this.myBetsSummary.refresh(true);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleCloseResultScreen$ = createEffect(() => {
        const _this = this;

        return this.actions$.pipe(
            ofType(BetslipResultActions.close),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, currentState]) => currentState.base.state === BetslipState.Result && currentState.result.result.slips.length > 0),
            concatMap(([_action, currentState]) => {
                const failedSlips = currentState.result.result.slips.filter(isFailedSlip);
                const keepInBetslip = this.keepInBetslip(currentState, failedSlips);

                const removablePickIds = _this.getRemovablePicks(currentState, keepInBetslip);
                const actions: Action[] = [];

                if (removablePickIds.length) {
                    actions.push(BetslipPickActions.requestRemoveMultiplePicks({ pickIds: removablePickIds }));
                    actions.push(BetslipPickActions.removedMultiplePicks({ pickIds: removablePickIds }));
                    actions.push(BetslipPickActions.requestRemovePlacedPicksFromGroup({ pickIds: removablePickIds }));
                }

                if (keepInBetslip) {
                    actions.push(RewardTokensActions.triggerAccaBoostRefresh());
                }
                if (currentState.types.base.currentSelectedType === BetslipType.EditBet) {
                    //yield BetslipEditBetActions.exitEditBet();
                    //yield BetslipActions.init();
                } else {
                    actions.push(BetslipActions.setState({ state: BetslipState.Edit }));
                }

                if (_this.removeStakesInLinearBetslip(currentState)) {
                    const succeededSlips = currentState.result.result.slips.filter(isSucceededNonPartialSlip);
                    const removeStakeActions = _this.getRemoveStakeActions(succeededSlips, currentState.result);

                    actions.push(...removeStakeActions);
                }

                return actions;
            }),
        );
    });

    handleClearAppliedRewards$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTeaserBetPlaceBetActions.placeBetSucceeded),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, state]) => state.result.result.slips.length > 0),
            concatMap(function* ([_action, state]): Iterable<Action> {
                const succeededSlips = state.result.result.slips.filter(isSucceededSlip);
                if (succeededSlips?.length > 0) {
                    yield RewardTokensActions.deselectAllToken();
                }
            }),
        );
    });

    handleSummaryViewModel$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPlaceBetActions.placeBetSucceeded, QuickBetActions.finishPlaceBet, BetslipTeaserBetPlaceBetActions.placeBetDone),
            map((action) => {
                let successSlips: (ISucceededSlipResult | ISucceededPartialSlipResult)[];
                if ('result' in action) {
                    successSlips = action.result.slips.filter(isSucceededSlip);
                } else if ('placeBetResult' in action) {
                    successSlips = action.placeBetResult.slips.filter(isSucceededSlip);
                } else {
                    successSlips = action.payload.flatMap((t) => t.slips).filter(isSucceededSlip);
                }

                return successSlips;
            }),
            filter((successSlips) => successSlips.length > 0),
            map((successSlips) =>
                BetslipPlaceBetActions.updateSummaryViewModel({
                    summary: this.resultSummaryService.buildSummaryViewModel(successSlips),
                }),
            ),
        );
    });

    handleSuccessfulBetPlacement$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(QuickBetActions.finishPlaceBet, BetslipPlaceBetActions.finishPlaceBet, BetslipTeaserBetPlaceBetActions.placeBetDone),
                map((action) => {
                    let successSlips: ISucceededSlipResult[];
                    if ('placeBetResult' in action) {
                        successSlips = action.placeBetResult.slips.filter(isSucceededNonPartialSlip);
                    } else {
                        successSlips = action.payload.flatMap((t) => t.slips).filter(isSucceededNonPartialSlip);
                    }

                    return successSlips;
                }),
                filter((successSlips) => successSlips.length > 0),
                tap((_) => this.dispatcher.dispatch(BaseBetslipEvents.BetPlaceSuccess)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleUserLoggedOutEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UserActions.userLoggedOut),
            map(() => BetslipResultActions.clearResult()),
        );
    });

    placeTeaserEmptyResult$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPlaceBetActions.startedPlaceBet),
            map(() => BetslipResultActions.clearResult()),
        );
    });

    handleGoToMyBetsCloseResultEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipResultActions.goToMyBets),
            map(() => BetslipResultActions.close()),
        );
    });

    handleGoToMyBetsNavigateEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipResultActions.goToMyBets),
                tap(() => {
                    if (this.epcotConfig.isEnabled) {
                        this.popupManager.open(ModelPopupTypes.MyBets);
                    } else if (this.popupManager.isPopupOpen('betslip')) {
                        this.redirectHelper.goToMyBets(MyBetsUrlParam.Open);
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    private getRemoveStakeActions(succeededSlips: ISucceededSlipResult[], resultState: IBetslipResultState): Action[] {
        const actions: Action[] = [];

        succeededSlips.forEach((slip) => {
            switch (slip.betType) {
                case BetSlipType.Single:
                    if (!resultState.keepPlacedBetsInLinearSingle) {
                        actions.push(
                            BetslipSingleBetActions.setSingleBetPickStake({
                                pickId: slip.picks[0].pickId.id,
                                stake: null,
                                silent: true,
                            }),
                        );
                    }
                    break;
                case BetSlipType.Combo:
                    if (isBetBuilderSucceededSlip(slip)) {
                        const groupId = slip.picks[0].clientPick!.id.id;

                        if (!resultState.keepPlacedBetsInLinearBetBuilder[groupId]) {
                            actions.push(BetbuilderActions.setPickStake({ pickId: groupId, stake: null, silent: true }));
                        }
                        break;
                    }

                    if (isTeaserSucceededSlip(slip)) {
                        if (!resultState.keepPlacedBetsInLinearTeaser) {
                            BetslipTeaserActions.setTeaserBetStake({ stake: null, stakeStr: null, silent: true });
                        }

                        break;
                    }

                    if (!resultState.keepPlacedBetsInLinearCombo) {
                        actions.push(BetslipComboBetActions.setComboBetStake({ stake: null, silent: true }));
                    }
                    break;
                case BetSlipType.System:
                    if (!resultState.keepPlacedBetsInLinearSystem[slip.systemKey!]) {
                        actions.push(BetslipSystemBetActions.removeSystemBetTypeStake({ systemTypeKey: slip.systemKey! }));
                    }
                    break;
            }
        });

        return actions;
    }

    private getRemovablePicks(state: IBetslipState, keepInBetslip: boolean): PickId[] {
        const succeededSlips = state.result.result.slips.filter(isSucceededNonPartialSlip);

        if (succeededSlips.length) {
            const mappedSlips = succeededSlips.flatMap((slip) => slip.picks);
            const filteredSlips = keepInBetslip ? mappedSlips.filter((pick) => pick.clientPick?.isNewCustomerOfferPick) : mappedSlips;

            return filteredSlips.map((pick) => pick.clientPick?.id ?? pick.pickId);
        }

        const localPicks = keepInBetslip ? state.picks.pickList.filter((pick) => pick.isNewCustomerOfferPick) : state.picks.pickList;

        return localPicks.map((pick) => pick.id);
    }

    private keepInBetslip(state: IBetslipState, failedSlips: IFailedSlipResult[]): boolean {
        if (state.base.displayMode === BetslipDisplayMode.Tabbed) {
            return state.result.keepPlacedBetsInBetslip;
        }

        return (
            !!failedSlips.length ||
            state.result.keepPlacedBetsInLinearSingle ||
            state.result.keepPlacedBetsInLinearCombo ||
            state.result.keepPlacedBetsInLinearTeaser ||
            !!Object.values(state.result.keepPlacedBetsInLinearSystem).find((value) => value) ||
            !!Object.values(state.result.keepPlacedBetsInLinearBetBuilder).find((value) => value)
        );
    }

    private removeStakesInLinearBetslip(state: IBetslipState): boolean {
        if (state.base.displayMode === BetslipDisplayMode.Tabbed) {
            return false;
        }

        const succeededSlips = state.result.result.slips.filter(isSucceededSlip);
        const failedSlips = state.result.result.slips.filter(isFailedSlip);

        if (!succeededSlips.length || !this.keepInBetslip(state, failedSlips)) {
            return false;
        }

        return (
            !state.result.keepPlacedBetsInLinearSingle ||
            !state.result.keepPlacedBetsInLinearCombo ||
            !state.result.keepPlacedBetsInLinearTeaser ||
            !!Object.values(state.result.keepPlacedBetsInLinearSystem).find((value) => !value) ||
            !!Object.values(state.result.keepPlacedBetsInLinearBetBuilder).find((value) => !value)
        );
    }

    private successPlacedBetActions(
        result: IPlacedBetslipResult,
        picks: BetslipPick[],
        isLinearBetslip: boolean,
        showSportcastAsCombo: boolean,
    ): Observable<Action> {
        return new Observable<Action>((subscriber) => {
            // Even if the bet placement is successful some slips may failed so we need to update them
            const updateActions = this.betslipErrorBetslipUpdaterService.getUpdateEventsFromPlaceBet(result, picks);
            for (const updateAction of updateActions) {
                subscriber.next(updateAction);
            }
            this.eventBridgeService.notifyBetPlaced(true);
            subscriber.next(BetslipPlaceBetActions.placeBetSucceeded({ result }));
            subscriber.next(BetslipActions.setState({ state: BetslipState.Result }));
            subscriber.next(BetslipResultActions.showBetStationResult());
            subscriber.next(RewardTokensActions.refreshPostPlacement());
            subscriber.next(RewardTokensActions.removeSelectedTokens());
            subscriber.next(RewardTokensActions.deselectNonReusableTokens());

            const failedSlips = result.slips.filter(isFailedSlip);

            if (failedSlips.length > 0) {
                const { pickErrors, betslipErrors, slipErrors } = getFailedSlipErrors(failedSlips, isLinearBetslip, showSportcastAsCombo);

                subscriber.next(
                    BetslipErrorsActions.placeBetResponseErrors({
                        pickErrors,
                        betslipErrors,
                        slipErrors,
                    }),
                );
            }

            subscriber.complete();
        });
    }

    private failedPlacedBetActions(
        result: IPlacedBetslipResult,
        request: PlaceBetRequest,
        picks: BetslipPick[],
        isLinearBetslip: boolean,
        showSportcastAsCombo: boolean,
    ): Observable<Action> {
        const failedSlips = result.slips.filter(isFailedSlip);
        const { pickErrors, betslipErrors, slipErrors } = getFailedSlipErrors(failedSlips, isLinearBetslip, showSportcastAsCombo);

        return new Observable((subscriber) => {
            this.eventBridgeService.notifyBetPlaced(false);
            const platformError =
                betslipErrors.find((error) => error.platformError) ||
                Object.values(slipErrors)
                    .flatMap((e) => e)
                    .find((error) => error.platformError);

            if (platformError?.platformError.range === 'platform') {
                subscriber.next(BetslipPlaceBetActions.placeBetFailedBetslipResult({ slips: failedSlips }));
                subscriber.next(BetslipActions.setState({ state: BetslipState.Result }));
            } else {
                subscriber.next(BetslipActions.setState({ state: BetslipState.Edit }));
            }
            const overAskOfferSlip = failedSlips.find((s) => !!s.overAskOffer);
            if (overAskOfferSlip) {
                this.eventBridgeService.notifyBetConfirmation(picks, request.betSlips[0].betSlipType, request.betSlips[0].stake.amount);
                this.arcSubscription.unsubscribe();
                if (this.hideOverAsk) {
                    subscriber.next(BetslipOverAskActions.setOverAskOfferId({ offerId: overAskOfferSlip.overAskOffer!.overAskOfferId }));
                    subscriber.next(BetslipOverAskActions.rejectOffer({ reason: OverAskRejectReason.ArcUserProfileRestriction }));
                    placeBetResponseErrors();
                } else {
                    const overAskSlipErrors = getFailedSlipErrors([overAskOfferSlip], isLinearBetslip, showSportcastAsCombo);
                    const error = getStakeLimitErrorInfo(overAskSlipErrors);
                    const overAskOfferError = error.betslipStakeHintInfo ||
                        error.pickStakeHintInfos[0] || {
                            type: '',
                            newStakeHint: 0,
                        };
                    subscriber.next(
                        this.overAskService.createStartFlowAction(
                            overAskOfferSlip.overAskOffer!.overAskOfferId,
                            request.requestId,
                            overAskOfferError,
                        ),
                    );
                    subscriber.complete();
                }
            } else {
                this.overAskService.cleanUp();

                const updateActions = this.betslipErrorBetslipUpdaterService.getUpdateEventsFromPlaceBet(result, picks);
                for (const updateAction of updateActions) {
                    subscriber.next(updateAction);
                }

                placeBetResponseErrors();
            }

            function placeBetResponseErrors() {
                subscriber.next(
                    BetslipErrorsActions.placeBetResponseErrors({
                        pickErrors,
                        betslipErrors,
                        slipErrors,
                    }),
                );
                subscriber.next(BetslipPlaceBetActions.placeBetFailed({ reason: PlaceBetFailedReason.BetPlacement }));
                subscriber.next(RewardTokensActions.refresh());
                subscriber.complete();
            }
        });
    }

    handleRealityCheck$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipPlaceBetActions.placeBetSucceeded),
                first(),
                tap((_) => {
                    if (
                        this.realityCheckConfig.realityCheckEvent === RealityCheckEvent.OnBetPlacement ||
                        this.realityCheckConfig.realityCheckEvent === RealityCheckEvent.OnBetPlacementWithDuration
                    ) {
                        this.realityCheckService.setFirstBetPlacementCookie();
                        this.realityCheckService.scheduleNotification();
                    }
                }),
            );
        },
        { dispatch: false },
    );
}
